import type { FireCMSTranslations } from "@firecms/core";

import { ja } from "./ja";
import { zhTW } from "./zh-TW";

/**
 * The subset of FireCMS's chrome strings this CMS translates.
 *
 * **`Required<Pick<...>>`, not `DeepPartial`.** FireCMS accepts a partial
 * bundle and falls back to English per key, which is what keeps this file 110
 * keys instead of 641 — but it means a key present in one locale and missing
 * in another silently renders English for that one user. Naming the subset in
 * a type instead makes that a compile error: add a string to `zh-TW.ts` and
 * `ja.ts` stops building until it has one too.
 *
 * The keys are drawn from `FireCMSTranslations`, so a FireCMS upgrade that
 * renames or removes one also fails the build rather than leaving a dead
 * translation nobody notices.
 *
 * To translate more, add the key names here first; both locale files then
 * refuse to compile until they cover it.
 */
export type CmsTranslations = Required<
    Pick<
        FireCMSTranslations,
        // Entity form actions
        | "save"
        | "create"
        | "create_copy"
        | "save_and_close"
        | "create_copy_and_close"
        | "create_and_close"
        | "discard"
        | "clear"
        | "cancel"
        | "edit"
        | "copy"
        | "delete"
        | "close"
        | "done"
        | "ok"
        | "add"
        | "remove"
        | "update"
        | "back"
        | "next"
        | "retry"
        | "copy_id"
        | "copied"
        // Confirmation dialogs
        | "delete_confirmation_title"
        | "delete_confirmation_body"
        | "delete_multiple_confirmation_body"
        | "delete_entity_confirm_title"
        | "unsaved_changes_title"
        | "unsaved_changes_body"
        | "discard_changes"
        | "keep_editing"
        | "are_you_sure_leave"
        | "unsaved_changes"
        | "unsaved_changes_in_entity"
        | "delete_selected"
        | "cannot_delete_selected"
        // Collection table
        | "search"
        | "find_by_id"
        | "find_entity_by_id"
        | "filter"
        | "filters"
        | "clear_filter"
        | "clear_filter_sort"
        | "clear_sort"
        | "no_items"
        | "no_entries_found"
        | "all_entries_loaded"
        | "create_your_first_entry"
        | "no_results_filter_sort"
        | "no_results"
        | "refresh_data"
        | "so_empty"
        | "multiple_entities"
        | "loading"
        | "column_cannot_be_edited"
        // Errors and validation
        | "required"
        | "this_form_has_errors"
        | "error"
        | "error_saving_data"
        | "saving_data"
        | "entities_saved"
        | "internal_error"
        | "this_entity_not_exist"
        | "entity_not_found"
        | "error_deleting"
        | "error_before_delete"
        | "this_form_has_been_modified"
        | "value_updated_elsewhere"
        // Data types
        | "type"
        | "string"
        | "number"
        | "boolean"
        | "date"
        | "map"
        | "array"
        // Reference picker (the tag field on news)
        | "reference_not_set"
        | "reference_does_not_exist"
        | "value_is_not_reference"
        | "click_to_edit"
        | "data_is_not_array_of_references"
        | "collection_does_not_exist"
        | "add_specific"
        | "select_specific"
        | "select_from"
        | "add_entry"
        | "add_on_top"
        | "add_below"
        | "add_to_field"
        // Navigation and chrome
        | "open_menu"
        | "close_drawer"
        | "navigation_drawer"
        | "home"
        | "log_out"
        | "search_collections"
        | "page_not_found"
        | "page_not_found_body"
        | "back_to_home"
        | "views_group"
        | "no_collections_found"
        // Theme
        | "dark_mode"
        | "light_mode"
        | "system_mode"
        // Misc
        | "copy_url_to_clipboard"
        | "open_image_in_new_tab"
        | "id"
        | "created_on"
        | "email"
        | "yes"
        | "no"
        | "all"
    >
>;

/** BCP-47 tags. `en` needs no bundle — it is FireCMS's own baseline. */
export const cmsLocales = ["zh-TW", "ja", "en"] as const;

export type CmsLocale = (typeof cmsLocales)[number];

/**
 * What the switcher shows. Each language is named in itself, never translated —
 * someone who has landed in the wrong locale needs to recognise their own
 * language, not read the current one's word for it.
 */
export const localeLabels: Record<CmsLocale, string> = {
    "zh-TW": "繁體中文",
    ja: "日本語",
    en: "English",
};

/**
 * The default for an editor who has never chosen.
 *
 * `FireCMSi18nProvider` prefers `localStorage` over this prop whenever a
 * preference exists, so this only decides the first visit. Traditional Chinese,
 * matching the language the collection names are already written in and the
 * language the LP comps are authored in.
 */
export const defaultCmsLocale: CmsLocale = "zh-TW";

/** Passed straight to `FireCMSi18nProvider`'s `translations` prop. */
export const cmsTranslations: Partial<Record<CmsLocale, CmsTranslations>> = {
    "zh-TW": zhTW,
    ja,
};
